<template>
	<div
		class="announcement-preview"
		:class="{ active, unread: announcement.isUnread }"
	>
		<div class="announcement-target">
			<AnnouncementTargets :announcement="announcement" withIcon />
		</div>
		<div class="announcement-sender-container">
			<AnnouncementSender
				:announcement="announcement"
				showDate
				profileSize="22px"
			/>
		</div>
		<div class="announcement-text-container">
			<PhotoVideoThumbnail
				class="announcement-media"
				v-if="media.isPhoto || media.isVideo"
				:media="media"
			/>
			<div
				class="announcement-text"
				v-text="announcement.text"
				:class="{ translated: announcement.isTranslated }"
				data-private
			/>
		</div>
		<div class="announcement-file" v-if="media.isFile">
			<MediaFile :media="media" noDownload />
		</div>
	</div>
</template>

<script>
import { getAnnouncementWithSender, getOrganization, getChat } from '@/store'
import { PhotoVideoThumbnail } from '@/components/common'
import MediaFile from '@/components/MediaView/MediaFile'
import AnnouncementTargets from './AnnouncementTargets'
import AnnouncementSender from './AnnouncementSender'

export default {
	name: 'AnnouncementPreview',
	components: {
		MediaFile,
		AnnouncementTargets,
		AnnouncementSender,
		PhotoVideoThumbnail,
	},
	props: {
		announcementId: [Number, String],
		active: { type: Boolean, default: null },
	},
	data() {
		return {
			announcement: getAnnouncementWithSender(this.announcementId) || {},
			org: getOrganization() || {},
		}
	},
	created() {
		this.$onEventBus({
			'announcement-update': () => {
				this.announcement = getAnnouncementWithSender(this.announcementId) || {}
			},
			'org-update': () => {
				this.org = getOrganization()
			},
		})
	},
	computed: {
		isOrgType() {
			return !!this.announcement.targets?.organization_id
		},
		isGroupType() {
			return !!this.announcement.targets?.bubble_ids
		},
		groupTarget() {
			if (!this.isGroupType) return null
			return getChat(this.announcement.targets.bubble_ids[0])
		},
		hasMedia() {
			return this.announcement.hasMedia
		},
		media() {
			if (!this.hasMedia) return {}
			return this.announcement.media
		},
	},
}
</script>

<style lang="scss" scoped>
@use 'sass:math';
@import 'src/sass/variables';

.announcement-preview {
	cursor: pointer;
	@include flex-column;
	padding: 0 8px 0 12px;
	border-radius: 8px;
	margin-bottom: 12px;
	border-right: 4px solid transparent;
	border-bottom: 1px solid var(--neutral-100);
	background: var(--neutral-0);

	&:hover {
		border-right-color: var(--teal-200);
	}
	&.active {
		border-right-color: var(--teal-300);
	}
	&.unread {
		$dot-size: 12px;
		position: relative;
		&::before {
			content: ' ';
			position: absolute;
			width: $dot-size;
			height: $dot-size;
			border-radius: 100px;
			background: var(--red-400);
			top: -#{math.div($dot-size, 2)};
			right: -#{math.div($dot-size, 2)};
		}
	}

	.announcement-target {
		padding: 12px 0;
		border-bottom: 1px solid var(--neutral-200);
		:deep(.announcement-targets) {
			.target-name {
				@include fontFamily(900);
				font-size: 14px;
			}
			.target-more {
				color: var(--neutral-500);
				font-size: 12px;
			}
		}
	}
	.announcement-sender-container {
		border-bottom: 1px solid var(--neutral-200);
		:deep(.announcement-sender) {
			height: 40px;
		}
	}
	.announcement-text-container {
		@include flex(12px);
		align-items: flex-start;
		padding: 12px 0;
		.announcement-text {
			@include clamp(3);
			font-size: 12px;
			color: var(--neutral-600);
			line-height: 1.25;

			&.translated {
				margin: -1px -3px;
				padding: 1px 3px;
				border-radius: 8px;
				background: var(--blue-100);
			}
		}
		.announcement-media {
			font-size: 43px;
		}
	}
	.announcement-file {
		border-top: 1px solid var(--neutral-200);
		padding: 12px 0;
		:deep(.attachment-wrapper) {
			padding: 0 !important;
			border: 0 !important;
			gap: 8px;
			.attachment-icon {
				max-height: 26px;
				overflow: hidden;
			}
			.attachment-title {
				@include ellipsis;
				font-size: 12px;
				color: var(--neutral-500) !important;
			}
			.attachment-size {
				display: none;
			}
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';
@include overrideScheme('dark') {
	.announcement-preview {
		.announcement-text {
			&.translated {
				background: rgba(73, 164, 218, 0.15) !important;
			}
		}
	}
}
</style>
